import { PathSet, PiecePath, Position } from '../types';

export type PieceKind = 'players' | 'opponents' | 'goalkeepers' | 'ball';

// Recording/serialization budget for one piece path. Paths are stored in
// pool-metre coordinates inside Play snapshots, so every point costs DynamoDB
// space for every saved play — keep trails compact.
export const MAX_PATH_POINTS = 64;
// Points closer than this (metres) to the previously recorded point are
// dropped while sampling a drag.
export const MIN_SAMPLE_DISTANCE = 0.15;
// Simplification tolerance (metres) for Ramer–Douglas–Peucker on drag stop.
export const SIMPLIFY_EPSILON = 0.12;
// A recorded trail whose arc length exceeds the straight start->end distance
// by less than this factor is visually a straight line — not worth storing.
export const MEANINGFUL_LENGTH_RATIO = 1.02;
// Endpoint mismatch (metres) below which anchorPath/appendable checks treat
// two points as "the same spot".
export const ANCHOR_EPSILON = 0.5;

const distance = (p1: Position, p2: Position): number => {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
};

export const getPathLength = (points: Position[]): number => {
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    length += distance(points[i - 1], points[i]);
  }
  return length;
};

// Cumulative arc lengths: result[i] = polyline length from points[0] to
// points[i]. Precompute once per transition so per-frame sampling stays cheap.
export const getCumulativeLengths = (points: Position[]): number[] => {
  const cumulative = new Array<number>(points.length);
  cumulative[0] = 0;
  for (let i = 1; i < points.length; i++) {
    cumulative[i] = cumulative[i - 1] + distance(points[i - 1], points[i]);
  }
  return cumulative;
};

// Position along the polyline at normalized arc-length t (0 = start, 1 = end).
// Constant-speed traversal: equal t steps cover equal distances, regardless
// of how densely the trail was sampled. Pass precomputed cumulative lengths
// when sampling repeatedly (playback ticks).
export const getPositionAlongPath = (
  points: Position[],
  t: number,
  cumulativeLengths?: number[],
): Position => {
  if (points.length === 0) {
    return { x: 0, y: 0 };
  }
  if (points.length === 1 || t <= 0) {
    return { ...points[0] };
  }
  if (t >= 1) {
    return { ...points[points.length - 1] };
  }

  const cumulative = cumulativeLengths || getCumulativeLengths(points);
  const totalLength = cumulative[cumulative.length - 1];
  if (totalLength === 0) {
    return { ...points[0] };
  }
  const target = t * totalLength;

  // Binary search for the segment containing the target arc length.
  let lo = 0;
  let hi = cumulative.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (cumulative[mid] <= target) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  const segmentLength = cumulative[hi] - cumulative[lo];
  const segmentT =
    segmentLength === 0 ? 0 : (target - cumulative[lo]) / segmentLength;
  return {
    x: points[lo].x + (points[hi].x - points[lo].x) * segmentT,
    y: points[lo].y + (points[hi].y - points[lo].y) * segmentT,
  };
};

// Perpendicular distance of point p from the line through a and b.
const perpendicularDistance = (
  p: Position,
  a: Position,
  b: Position,
): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return distance(p, a);
  }
  return (
    Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x) /
    Math.sqrt(lengthSquared)
  );
};

const rdp = (points: Position[], epsilon: number): Position[] => {
  if (points.length < 3) {
    return points;
  }
  let maxDistance = 0;
  let maxIndex = 0;
  const first = points[0];
  const last = points[points.length - 1];
  for (let i = 1; i < points.length - 1; i++) {
    const d = perpendicularDistance(points[i], first, last);
    if (d > maxDistance) {
      maxDistance = d;
      maxIndex = i;
    }
  }
  if (maxDistance <= epsilon) {
    return [first, last];
  }
  const left = rdp(points.slice(0, maxIndex + 1), epsilon);
  const right = rdp(points.slice(maxIndex), epsilon);
  return left.slice(0, -1).concat(right);
};

// Resample a polyline to at most maxPoints points, spaced evenly by arc
// length. Endpoints are preserved exactly.
const resample = (points: Position[], maxPoints: number): Position[] => {
  if (points.length <= maxPoints) {
    return points;
  }
  const cumulative = getCumulativeLengths(points);
  const result: Position[] = [];
  for (let i = 0; i < maxPoints; i++) {
    result.push(
      getPositionAlongPath(points, i / (maxPoints - 1), cumulative),
    );
  }
  result[0] = { ...points[0] };
  result[maxPoints - 1] = { ...points[points.length - 1] };
  return result;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

// Compact a raw recorded trail for storage: Ramer–Douglas–Peucker, then a
// hard point cap, then 2-decimal rounding (centimetre precision in pool
// metres — far below visual resolution).
export const simplifyPath = (
  points: Position[],
  epsilon: number = SIMPLIFY_EPSILON,
): Position[] => {
  const simplified = resample(rdp(points, epsilon), MAX_PATH_POINTS);
  return simplified.map((p) => ({ x: round2(p.x), y: round2(p.y) }));
};

// A path is worth storing only when it visibly deviates from the straight
// line between its endpoints.
export const isPathMeaningful = (points: Position[]): boolean => {
  if (points.length < 3) {
    return false;
  }
  const straight = distance(points[0], points[points.length - 1]);
  const arc = getPathLength(points);
  if (straight < MIN_SAMPLE_DISTANCE) {
    // Returned (almost) to the start: keep only genuinely long round trips.
    return arc > 4 * MIN_SAMPLE_DISTANCE;
  }
  return arc > straight * MEANINGFUL_LENGTH_RATIO;
};

// Re-anchor a stored path onto the actual transition endpoints. Snapshot
// edits (re-drag, frame remove/reorder) can leave a saved path whose ends no
// longer match the snapshot positions; prepending/appending the real
// endpoints degrades that into linear lead-in/out segments instead of a
// teleport.
export const anchorPath = (
  points: Position[],
  start: Position,
  end: Position,
): Position[] => {
  let anchored = points;
  if (points.length === 0) {
    return [start, end];
  }
  if (distance(points[0], start) > ANCHOR_EPSILON) {
    anchored = [start, ...anchored];
  }
  if (distance(anchored[anchored.length - 1], end) > ANCHOR_EPSILON) {
    anchored = [...anchored, end];
  }
  return anchored;
};

export const reversePath = (points: Position[]): Position[] =>
  [...points].reverse();

// True when a new drag starting at dragStart continues the stored path (D8
// policy: append instead of replace).
export const isPathContinuation = (
  stored: PiecePath | null | undefined,
  dragStart: Position,
): boolean => {
  if (!stored || stored.points.length === 0) {
    return false;
  }
  return (
    distance(stored.points[stored.points.length - 1], dragStart) <=
    ANCHOR_EPSILON
  );
};

export const getPathSetEntry = (
  paths: PathSet | null | undefined,
  kind: PieceKind,
  index: number,
): PiecePath | null => {
  if (!paths) {
    return null;
  }
  if (kind === 'ball') {
    return paths.ball || null;
  }
  return paths[kind]?.[index] || null;
};

const hasAnyPath = (paths: PathSet): boolean => {
  if (paths.ball) {
    return true;
  }
  return (['players', 'opponents', 'goalkeepers'] as const).some((kind) =>
    paths[kind]?.some(Boolean),
  );
};

// Fold one finished drag into a snapshot's path set (the D8 policy):
// - path mode + curved trail: store it; a drag starting where the stored
//   path ends extends it instead of replacing (curve + small follow-up
//   adjustment keeps the curve);
// - path mode + essentially straight trail: clear the piece's entry;
// - linear mode: clear the piece's entry (its endpoint changed, the stored
//   path no longer applies).
// rawTrail must start at the piece's pre-drag position; endPosition is the
// authoritative final position computed by the drag-stop math. Returns the
// next path set, or null when no piece has a path left.
export const applyDragToPathSet = (
  currentPaths: PathSet | null | undefined,
  kind: PieceKind,
  index: number,
  rawTrail: Position[],
  endPosition: Position,
  isPathMode: boolean,
): PathSet | null => {
  const existing = getPathSetEntry(currentPaths, kind, index);

  let entry: PiecePath | null = null;
  if (isPathMode && rawTrail.length >= 2) {
    let points = [...rawTrail];
    // The trail is sampled with MIN_SAMPLE_DISTANCE thinning, so its last
    // point may lag the real drop position — make the endpoint exact.
    if (
      distance(points[points.length - 1], endPosition) <= MIN_SAMPLE_DISTANCE
    ) {
      points[points.length - 1] = endPosition;
    } else {
      points.push(endPosition);
    }
    if (existing && isPathContinuation(existing, rawTrail[0])) {
      points = [...existing.points, ...points];
    }
    const simplified = simplifyPath(points);
    entry = isPathMeaningful(simplified) ? { points: simplified } : null;
  }

  if (!entry && !existing) {
    // Nothing to store and nothing to clear.
    return currentPaths || null;
  }

  const next: PathSet = { ...(currentPaths || {}) };
  if (kind === 'ball') {
    next.ball = entry;
  } else {
    const list: (PiecePath | null)[] = [...(next[kind] || [])];
    while (list.length <= index) {
      list.push(null);
    }
    list[index] = entry;
    next[kind] = list;
  }
  return hasAnyPath(next) ? next : null;
};
